import { useEffect, useState } from 'react'
import { Card, CardContent, cn } from '@saas-pages/shared/ui'
import {
  buildProductionCalendarMonths,
  productionCalendarYearRoute,
  type ApprovedProductionCalendar,
  type ProductionCalendarDay,
} from '@/shared/production-calendar'

const MONTHS = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
] as const
const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'] as const

interface ProductionCalendarHubPageProps {
  calendars: readonly ApprovedProductionCalendar[]
}

interface ProductionCalendarYearPageProps {
  calendar: ApprovedProductionCalendar
}

export function ProductionCalendarHubPage({ calendars }: ProductionCalendarHubPageProps) {
  return (
    <div className="mx-auto max-w-5xl">
      <Hero
        eyebrow="Производственный календарь"
        title="Производственный календарь России"
        description="Федеральный календарь РФ для стандартной пятидневной недели. Данные составлены на основе официально опубликованных актов и публикуются только после независимой проверки."
      />

      <TodayStatus calendars={calendars} />

      <section aria-labelledby="available-years" className="grid gap-4 md:grid-cols-2">
        <Card className="overflow-hidden">
          <div aria-hidden="true" className="h-1.5 bg-gradient-to-r from-cyan-400 via-primary to-emerald-400" />
          <CardContent className="p-5 sm:p-6">
            <h2 id="available-years" className="text-xl font-semibold">Доступные годы</h2>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              В списке есть только годы со статусом approved. Черновые и непроверенные данные не публикуются и не индексируются.
            </p>
            <div className="mt-5 grid gap-3">
              {calendars.map((calendar) => (
                <a
                  key={calendar.year}
                  href={productionCalendarYearRoute(calendar.year)}
                  className="group flex items-center justify-between gap-4 rounded-lg border bg-background p-4 transition-colors hover:border-primary/50 hover:bg-accent"
                >
                  <span>
                    <span className="block text-lg font-semibold">Производственный календарь {calendar.year}</span>
                    <span className="mt-1 block text-sm text-muted-foreground">
                      {calendar.aggregates.workingDays} рабочих · {calendar.aggregates.nonWorkingDays} нерабочих дней
                    </span>
                    <span className="mt-1 block text-xs text-muted-foreground">
                      Статус: {calendar.status} · Проверено {calendar.lastVerifiedAt}
                    </span>
                  </span>
                  <span className="shrink-0 text-sm font-medium text-primary">Открыть →</span>
                </a>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5 sm:p-6">
            <h2 className="text-xl font-semibold">Что покрывает календарь</h2>
            <ul className="mt-4 space-y-3 text-sm leading-6 text-muted-foreground">
              <li><strong className="text-foreground">Территория:</strong> федеральные правила Российской Федерации.</li>
              <li><strong className="text-foreground">График:</strong> стандартная пятидневка с выходными в субботу и воскресенье.</li>
              <li><strong className="text-foreground">Учитывается:</strong> федеральные праздники и утверждённые переносы выходных.</li>
              <li><strong className="text-foreground">Не учитывается:</strong> нормы рабочего времени в часах.</li>
            </ul>
          </CardContent>
        </Card>
      </section>

      <section className="mt-8 rounded-xl border bg-card p-5 shadow-sm sm:p-6">
        <h2 className="text-xl font-semibold">Методология и источники</h2>
        <p className="mt-3 text-sm leading-6 text-muted-foreground">
          Для каждого года dateify применяет базовую неделю понедельник–пятница, отмечает федеральные нерабочие праздники по статье 112 ТК РФ и затем применяет финальные статусы дат из опубликованного постановления Правительства о переносах. Источники и результаты второй ручной сверки хранятся вместе с версией календаря.
        </p>
        <div className="mt-4 flex flex-wrap gap-3 text-sm">
          <a className="font-medium text-primary underline-offset-4 hover:underline" href="https://pravo.gov.ru/proxy/ips/?docbody=&nd=102074279" rel="noreferrer" target="_blank">
            Статья 112 ТК РФ
          </a>
          <a className="font-medium text-primary underline-offset-4 hover:underline" href="https://publication.pravo.gov.ru/document/0001202509240023" rel="noreferrer" target="_blank">
            Постановление № 1466
          </a>
          <a className="font-medium text-primary underline-offset-4 hover:underline" href="https://government.ru/news/56309/" rel="noreferrer" target="_blank">
            Пояснение Правительства
          </a>
        </div>
      </section>

      <div className="mt-6 flex flex-col gap-3 rounded-lg border bg-card p-5 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="font-semibold">Посчитать рабочие дни между датами</h2>
          <p className="mt-1 text-sm leading-6 text-muted-foreground">
            По умолчанию калькулятор считает нерабочими только субботу и воскресенье. Для дат с полным approved-покрытием доступен федеральный режим с праздниками и переносами.
          </p>
        </div>
        <a className="inline-flex min-h-10 shrink-0 items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground" href="/rabochie-dni-mezhdu-datami">
          Открыть калькулятор
        </a>
      </div>

      <Guardrail />

      <section className="mt-10 border-t pt-8">
        <h2 className="text-2xl font-semibold">Частые вопросы</h2>
        <div className="mt-4 grid gap-3 lg:grid-cols-3">
          <Faq title="Это официальный календарь?">
            Нет. Календарь dateify составлен на основе официально опубликованных актов и имеет справочный характер.
          </Faq>
          <Faq title="Почему доступны не все годы?">
            Год появляется только после публикации акта о переносах, второй независимой сверки и перевода набора данных в approved.
          </Faq>
          <Faq title="Подойдёт ли он для моего графика?">
            Только для федеральной пятидневки. Региональные праздники, шестидневка, сменный и индивидуальный график могут дать другой результат.
          </Faq>
        </div>
      </section>
    </div>
  )
}

export function ProductionCalendarYearPage({ calendar }: ProductionCalendarYearPageProps) {
  const months = buildProductionCalendarMonths(calendar)
  const resolution = calendar.sources.find((source) => source.kind === 'resolution')

  return (
    <div className="mx-auto max-w-7xl">
      <Hero
        eyebrow="Федеральный календарь РФ · стандартная пятидневка"
        title={`Производственный календарь на ${calendar.year} год`}
        description={`Календарь на ${calendar.year} год составлен на основе официально опубликованных актов: федеральных праздников и утверждённых переносов выходных.`}
      />

      <section className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4" aria-label="Статус и итоги календаря">
        <SummaryCard label="Статус данных" value={calendar.status} tone="approved" />
        <SummaryCard label="Проверено" value={calendar.lastVerifiedAt} />
        <SummaryCard label="Рабочих дней" value={String(calendar.aggregates.workingDays)} />
        <SummaryCard label="Нерабочих дней" value={String(calendar.aggregates.nonWorkingDays)} />
      </section>

      {resolution && (
        <aside className="mb-6 rounded-lg border border-primary/30 bg-primary/5 p-4 text-sm leading-6">
          <p className="font-semibold">Акт о переносах</p>
          <a className="mt-1 inline-block text-primary underline-offset-4 hover:underline" href={resolution.url} rel="noreferrer" target="_blank">
            {resolution.title}
          </a>
          <p className="mt-2 text-muted-foreground">
            Проверено {calendar.lastVerifiedAt}. Дата проверки показывает последнюю ручную сверку и не гарантирует отсутствие более поздних изменений.
          </p>
        </aside>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-4 text-sm text-muted-foreground" aria-label="Условные обозначения">
        <span className="inline-flex items-center gap-2"><span aria-hidden="true" className="h-5 w-5 rounded border bg-card" />Рабочий день</span>
        <span className="inline-flex items-center gap-2"><span aria-hidden="true" className="h-5 w-5 rounded bg-primary/15" />Нерабочий день</span>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {months.map((month) => (
          <MonthGrid key={month.month} year={calendar.year} month={month.month} days={month.days} workingDays={month.workingDays} nonWorkingDays={month.nonWorkingDays} />
        ))}
      </div>

      <div className="mt-6 flex flex-col gap-3 rounded-lg border bg-card p-5 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="font-semibold">Нужно посчитать дни между датами?</h2>
          <p className="mt-1 text-sm text-muted-foreground">По умолчанию калькулятор считает нерабочими только субботу и воскресенье. Для дат с полным approved-покрытием доступен федеральный режим с праздниками и переносами.</p>
        </div>
        <a className="inline-flex min-h-10 items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground" href="/rabochie-dni-mezhdu-datami">
          Открыть калькулятор
        </a>
      </div>

      <Guardrail />
    </div>
  )
}

function toLocalIsoDate(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function findCalendarDay(
  calendars: readonly ApprovedProductionCalendar[],
  isoDate: string,
): ProductionCalendarDay | null {
  const year = Number(isoDate.slice(0, 4))
  const calendar = calendars.find((item) => item.year === year)
  return calendar?.days.find((day) => day.date === isoDate) ?? null
}

function dayStatusLabel(day: ProductionCalendarDay | null): string {
  if (!day) return 'календарь на этот год ещё не проверен'
  return day.status === 'working' ? 'рабочий день' : 'нерабочий день'
}

function TodayStatus({ calendars }: { calendars: readonly ApprovedProductionCalendar[] }) {
  // The date depends on the visitor's clock, so it stays out of the prerendered HTML.
  const [now, setNow] = useState<Date | null>(null)

  useEffect(() => {
    setNow(new Date())
  }, [])

  const today = now ? findCalendarDay(calendars, toLocalIsoDate(now)) : null
  const tomorrowDate = now ? new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1) : null
  const tomorrow = tomorrowDate ? findCalendarDay(calendars, toLocalIsoDate(tomorrowDate)) : null

  return (
    <section aria-labelledby="today-status" className="mb-4 rounded-lg border border-primary/30 bg-primary/5 p-4 sm:p-5">
      <h2 id="today-status" className="text-base font-semibold">Сегодня рабочий день или нет?</h2>
      {now ? (
        <p className="mt-1 text-2xl font-bold tracking-normal first-letter:uppercase" role="status">
          {new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long' }).format(now)}: {dayStatusLabel(today)}
        </p>
      ) : (
        <p className="mt-1 text-sm leading-6 text-muted-foreground">
          Ответ появится здесь: блок сверяет дату на вашем устройстве с календарём.
        </p>
      )}
      <p className="mt-1 text-sm leading-6 text-muted-foreground">
        {now && `Завтра: ${dayStatusLabel(tomorrow)}. `}
        Для федеральной пятидневки, без региональных праздников и графика работодателя.
      </p>
    </section>
  )
}

function Hero({ eyebrow, title, description }: { eyebrow: string; title: string; description: string }) {
  return (
    <section className="pb-4 pt-1 sm:pb-8 sm:pt-4">
      <div className="mb-2 inline-flex rounded-md border bg-card px-2 py-0.5 text-xs font-medium text-muted-foreground shadow-sm sm:mb-3 sm:px-2.5 sm:py-1">{eyebrow}</div>
      <h1 className="text-2xl font-bold tracking-normal sm:text-4xl">{title}</h1>
      <p className="mt-1 line-clamp-2 max-w-3xl text-sm leading-6 text-muted-foreground sm:mt-2 sm:line-clamp-none sm:text-base sm:leading-7">{description}</p>
    </section>
  )
}

function SummaryCard({ label, value, tone }: { label: string; value: string; tone?: 'approved' }) {
  return (
    <div className="rounded-lg border bg-card p-4 shadow-sm">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={cn('mt-2 text-2xl font-semibold tabular-nums', tone === 'approved' && 'text-emerald-700 dark:text-emerald-300')}>{value}</p>
    </div>
  )
}

function MonthGrid({ year, month, days, workingDays, nonWorkingDays }: {
  year: number
  month: number
  days: readonly (ProductionCalendarDay | null)[]
  workingDays: number
  nonWorkingDays: number
}) {
  return (
    <article className="rounded-lg border bg-card p-3 shadow-sm sm:p-4">
      <table className="w-full table-fixed border-separate border-spacing-1 text-center">
        <caption className="mb-3 text-lg font-semibold">
          {MONTHS[month - 1]} {year}
          <span className="mt-1 block text-xs font-normal text-muted-foreground">{workingDays} раб. · {nonWorkingDays} нераб.</span>
        </caption>
        <thead>
          <tr>{WEEKDAYS.map((weekday, index) => <th key={weekday} scope="col" className={cn('py-1 text-xs font-medium text-muted-foreground', index >= 5 && 'text-primary')}>{weekday}</th>)}</tr>
        </thead>
        <tbody>
          {Array.from({ length: Math.ceil(days.length / 7) }, (_, row) => (
            <tr key={row}>
              {days.slice(row * 7, row * 7 + 7).map((day, column) => day ? (
                <td key={day.date} className={cn('h-8 rounded text-sm tabular-nums', day.status === 'non-working' && 'bg-primary/10 font-medium text-primary dark:bg-primary/20')}>
                  <time dateTime={day.date} aria-label={`${day.date}: ${day.status === 'working' ? 'рабочий день' : 'нерабочий день'}`}>{Number(day.date.slice(8, 10))}</time>
                </td>
              ) : <td key={`empty-${column}`} className="h-8" />)}
            </tr>
          ))}
        </tbody>
      </table>
    </article>
  )
}

function Guardrail() {
  return (
    <aside className="mt-6 rounded-lg border border-amber-400/40 bg-amber-100/50 p-4 text-sm leading-6 text-amber-950 dark:bg-amber-950/20 dark:text-amber-100">
      <p className="font-semibold">Справочная информация</p>
      <p className="mt-1">
        Календарь не учитывает региональные праздники, шестидневные, сменные и индивидуальные графики работодателя. Для решений о зарплате, кадровом учёте, сроках или конкретном графике сверяйтесь с календарём работодателя, региональными актами и действующим официальным законодательством.
      </p>
    </aside>
  )
}

function Faq({ title, children }: { title: string; children: string }) {
  return (
    <article className="rounded-lg border bg-card p-5 shadow-sm">
      <h3 className="font-semibold">{title}</h3>
      <p className="mt-2 text-sm leading-6 text-muted-foreground">{children}</p>
    </article>
  )
}
